'use client'

import type React from 'react'

import { useEffect, useState } from 'react'

import { BodySubtext, BodyXS } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

import { ToggleGroup, ToggleGroupItem } from './base'

interface SwitchOption {
  value: string
  label: string | React.ReactNode
}

export interface SwitchProps {
  onChange: (value: string) => void
  disabled?: boolean
  label?: string
  value: string
  options: SwitchOption[]
  disabledOptions?: string[]
  switchItemClassName?: string
}

/**
 * @deprecated Use `Switch` from `@moment/design-system-ui/switch` instead.
 */
// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function Switch({
  onChange,
  disabled = false,
  label,
  value: externalValue,
  options,
  disabledOptions,
  switchItemClassName,
}: SwitchProps) {
  const [internalValue, setInternalValue] = useState<string>(externalValue)

  useEffect(() => {
    setInternalValue(externalValue)
  }, [externalValue])

  const handleValueChange = (newValue: string) => {
    // When a user clicks an already selected toggle in a "single" type ToggleGroup,
    // Radix sends an empty string ('') as the new value. We intentionally ignore these
    // empty values to prevent users from deselecting/unswitching options.
    if (newValue !== '') {
      setInternalValue(newValue)
      onChange(newValue)
    }
    // Empty values are silently ignored to maintain that one option is always selected
  }

  return (
    <div className="w-full">
      {label && (
        <div className="mb-1">
          <BodyXS color="text-secondary">{label}</BodyXS>
        </div>
      )}
      <div className="bg-moment-white-200 dark:bg-moment-black-100 rounded-md">
        <ToggleGroup
          type="single"
          value={internalValue}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          {options.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              disabled={disabledOptions?.includes(option.value)}
              // this is to ensure that the toggle group items are equal width
              className={cn(switchItemClassName)}
            >
              {typeof option.label === 'string' ? (
                <BodySubtext>{option.label}</BodySubtext>
              ) : (
                <div className="flex items-center">{option.label}</div>
              )}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
    </div>
  )
}

Switch.displayName = 'Switch'
